import type { FeedHealthResponse } from './api';

export type FeedGroup = 'PRIMARY LIVE' | 'MODEL / PRIORS' | 'LEARNING / TEST' | 'OPTIONAL / LEGACY';
export type FeedTone = 'live' | 'ready' | 'degraded' | 'offline' | 'idle';

type Feed = FeedHealthResponse['feeds'][number];

export interface ConnectionPill {
  key: string;
  label: string;
  status: string;
  tone: FeedTone;
  detail: string;
}

export function findFeed(health: FeedHealthResponse | undefined, name: string): Feed | undefined {
  return health?.feeds.find((feed) => feed.name === name);
}

export function feedTone(status: string | null | undefined): FeedTone {
  const value = status?.trim().toUpperCase() ?? 'UNKNOWN';
  if (['LIVE', 'OK', 'APPLIED'].includes(value)) return 'live';
  if (['READY', 'CACHED', 'LEARNING', 'OPTIONAL', 'STANDBY', 'DISABLED', 'WARMING'].includes(value)) return 'ready';
  if (['STALE', 'DEGRADED'].includes(value)) return 'degraded';
  if (['DOWN', 'OFFLINE', 'UNAVAILABLE'].includes(value)) return 'offline';
  return 'idle';
}

export function freshnessLabel(feed: Feed | undefined): string {
  if (!feed) return 'NO SIGNAL';
  if (feed.freshnessSeconds === null) return feed.status;
  return `${feed.status} · ${feed.freshnessSeconds}s`;
}

function freshnessAge(feed: Feed | undefined): string {
  return feed?.freshnessSeconds === null || feed?.freshnessSeconds === undefined ? '' : ` · ${feed.freshnessSeconds}s`;
}

export function connectionPills(
  health: FeedHealthResponse | undefined,
  realtimeStatus: string,
  realtimeName: string,
  espnApplied = false,
): ConnectionPill[] {
  const fpl = findFeed(health, 'FPL Public JSON');
  const espn = findFeed(health, 'ESPN LIVE EDGE');
  const vaastav = findFeed(health, 'Vaastav Priors');
  const espnStatus = espnApplied
    ? `APPLIED${freshnessAge(espn)}`
    : ['LIVE', 'READY'].includes(espn?.status?.toUpperCase() ?? '')
      ? `READY${freshnessAge(espn)}`
      : freshnessLabel(espn);
  return [
    {
      key: 'fpl',
      label: 'FPL',
      status: freshnessLabel(fpl),
      tone: feedTone(fpl?.status),
      detail: 'OFFICIAL FPL TRUTH',
    },
    {
      key: 'espn',
      label: 'ESPN EDGE',
      status: espnStatus,
      tone: feedTone(espnApplied ? 'APPLIED' : espn?.status),
      detail: espnApplied ? 'APPLIED TO CURRENT MATCH CENTRE' : 'READY FOR SAFE SCORE · MINUTE · STATUS OVERLAY',
    },
    {
      key: 'realtime',
      label: realtimeName === 'WebSocket' ? 'WS' : 'RT',
      status: realtimeStatus,
      tone: feedTone(realtimeStatus),
      detail: 'TRANSPORT ONLY',
    },
    {
      key: 'priors',
      label: 'PRIORS',
      status: vaastav?.status ?? 'WARMING',
      tone: feedTone(vaastav?.status),
      detail: 'MODEL SUPPORT · NOT LIVE TRUTH',
    },
  ];
}

export function feedGroup(name: string): FeedGroup {
  if (['FPL Public JSON', 'FREE Match Data', 'ESPN LIVE EDGE', 'Realtime'].includes(name)) return 'PRIMARY LIVE';
  if (['Vaastav Priors', 'The Odds API', 'Football-Data.co.uk'].includes(name)) return 'MODEL / PRIORS';
  if (['fplcache Learning', 'BRAM-X SIM'].includes(name)) return 'LEARNING / TEST';
  return 'OPTIONAL / LEGACY';
}

export function groupFeeds(health: FeedHealthResponse | undefined): Array<{ group: FeedGroup; feeds: Feed[] }> {
  const order: FeedGroup[] = ['PRIMARY LIVE', 'MODEL / PRIORS', 'LEARNING / TEST', 'OPTIONAL / LEGACY'];
  return order.map((group) => ({ group, feeds: (health?.feeds ?? []).filter((feed) => feedGroup(feed.name) === group) }));
}

export function providerProvenance(sourceProvider: string): {
  official: string;
  liveEdge: string;
  resolved: string;
  fallback: string;
} {
  const source = sourceProvider.toLowerCase();
  const espn = source.includes('espn-live-edge');
  return {
    official: 'FPL Public JSON',
    liveEdge: espn ? 'ESPN LIVE EDGE · APPLIED' : 'ESPN LIVE EDGE · READY/FALLBACK',
    resolved: espn ? 'ESPN score/minute/status over FPL fixture' : 'FPL score/minute/status',
    fallback: 'FPL Public JSON · READY',
  };
}
